/** The live stream's format: 16 kHz, 16-bit little-endian, mono PCM after a 44-byte WAV header. */
export const SAMPLE_RATE_HZ = 16_000
export const WAV_HEADER_BYTES = 44

/** Audio queued ahead of playback, so network jitter doesn't cause gaps. */
export const PLAYBACK_CUSHION_SECONDS = 0.25

/** Samples gathered before scheduling a buffer (100 ms), so playback isn't thousands of tiny nodes. */
export const MIN_BUFFER_SAMPLES = 1600

/** Full scale of a signed 16-bit sample. */
const INT16_SCALE = 32768

/**
 * Turns the /listen byte stream into float samples: skips the WAV header and decodes 16-bit
 * little-endian PCM, carrying a split sample over to the next chunk.
 */
export class PcmStreamDecoder {
    private headerRemaining = WAV_HEADER_BYTES
    private carry: number | undefined

    push(chunk: Uint8Array): Float32Array {
        let bytes = chunk
        if (this.headerRemaining > 0) {
            const skipped = Math.min(this.headerRemaining, bytes.length)
            this.headerRemaining -= skipped
            bytes = bytes.subarray(skipped)
        }
        if (this.carry !== undefined && bytes.length > 0) {
            const joined = new Uint8Array(bytes.length + 1)
            joined[0] = this.carry
            joined.set(bytes, 1)
            bytes = joined
            this.carry = undefined
        }
        const sampleCount = Math.floor(bytes.length / 2)
        if (bytes.length % 2 === 1) this.carry = bytes[bytes.length - 1]
        const view = new DataView(bytes.buffer, bytes.byteOffset, sampleCount * 2)
        const samples = new Float32Array(sampleCount)
        for (let i = 0; i < sampleCount; i++) samples[i] = view.getInt16(i * 2, true) / INT16_SCALE
        return samples
    }
}

export class LiveAudioError extends Error {
    override readonly name = 'LiveAudioError'
}

/**
 * Plays the virtual candidate's /listen stream through the Web Audio API as it arrives, and
 * exposes an AnalyserNode for visualization. The AudioContext must be created during a click, or
 * browsers keep it suspended.
 */
export class LiveAudioPlayer {
    readonly analyser: AnalyserNode
    private readonly abortController = new AbortController()
    private nextStartTime = 0
    private stopped = false
    private readonly context: AudioContext
    private readonly fetchImpl: typeof fetch

    // Explicit fields: this package's Babel build drops TypeScript constructor parameter properties.
    constructor(
        context: AudioContext,
        fetchImpl: typeof fetch = (input, init) => fetch(input, init)
    ) {
        this.context = context
        this.fetchImpl = fetchImpl
        this.analyser = context.createAnalyser()
        this.analyser.fftSize = 512
        this.analyser.smoothingTimeConstant = 0.8
        this.analyser.connect(context.destination)
    }

    /** Streams until the server ends the response (the interview ended) or stop() is called. */
    async play(url: string, idToken: string): Promise<void> {
        let response: Response
        try {
            response = await this.fetchImpl(url, {
                headers: { Authorization: `Bearer ${idToken}` },
                signal: this.abortController.signal,
            })
        } catch (error) {
            if (this.stopped) return
            throw new LiveAudioError(`Could not connect to the live audio: ${String(error)}`)
        }
        if (!response.ok || !response.body) {
            throw new LiveAudioError(`The live audio returned ${response.status}`)
        }
        const reader = response.body.getReader()
        const decoder = new PcmStreamDecoder()
        let pending: Float32Array[] = []
        let pendingSamples = 0
        const flush = () => {
            if (pendingSamples === 0) return
            const merged = new Float32Array(pendingSamples)
            let offset = 0
            for (const part of pending) {
                merged.set(part, offset)
                offset += part.length
            }
            this.schedule(merged)
            pending = []
            pendingSamples = 0
        }
        try {
            for (;;) {
                const { done, value } = await reader.read()
                if (done || this.stopped) break
                const samples = decoder.push(value)
                if (samples.length === 0) continue
                pending.push(samples)
                pendingSamples += samples.length
                if (pendingSamples >= MIN_BUFFER_SAMPLES) flush()
            }
            if (!this.stopped) flush()
        } catch (error) {
            if (!this.stopped) {
                throw new LiveAudioError(`The live audio stopped: ${String(error)}`)
            }
        }
    }

    /** Stops streaming and releases the audio device. */
    stop(): void {
        if (this.stopped) return
        this.stopped = true
        this.abortController.abort()
        void this.context.close().catch(() => undefined)
    }

    private schedule(samples: Float32Array): void {
        const buffer = this.context.createBuffer(1, samples.length, SAMPLE_RATE_HZ)
        buffer.getChannelData(0).set(samples)
        const source = this.context.createBufferSource()
        source.buffer = buffer
        source.connect(this.analyser)
        // After a stall, restart with a fresh cushion instead of playing in the past.
        const startAt = Math.max(
            this.nextStartTime,
            this.context.currentTime + PLAYBACK_CUSHION_SECONDS
        )
        source.start(startAt)
        this.nextStartTime = startAt + buffer.duration
    }
}
